// A service factory that creates a new service instance
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { BackendServiceName } from 'v2/types'
import type { RUserSession } from 'v2/types/api/auth'
import { logger } from 'v2/utils/logger'
import { getLocalAuth } from '../../utils'
import { createSSEStream, type StreamFunction } from '../../utils/sse-stream'
import { getEmbedSession, isEmbedMode } from '../utils/embed-session'
import {
  applyEmbedRequestConfig,
  createEmbedHeaders,
  handleEmbedUnauthorized,
} from './service-interceptors'
import { applyTdlcTransport, isTdlcApiTransportEnabled } from './tdlc-transport'

const GLOBAL_API_URL = process.env.REACT_APP_GLOBAL_AUTH
const REGIONAL_API_URL = process.env.REACT_APP_REGION_AUTH

export interface Service {
  instance: AxiosInstance
  get: <T>(
    path: string,
    params?: Record<string, unknown> | string,
    config?: AxiosRequestConfig,
  ) => Promise<T>
  post: <TApiResponse>(
    path: string,
    data?: unknown,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig,
  ) => Promise<TApiResponse>
  put: <TApiResponse>(path: string, data?: unknown) => Promise<TApiResponse>
  patch: <TApiResponse>(path: string, data?: unknown) => Promise<TApiResponse>
  delete: <T>(path: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>
  stream: StreamFunction
}

export const createGlobalHeaders = () => {
  // Embed iframe mode: identity comes from the sessionStorage embed session,
  // with Client-Type: embed_app and no Aom_uuid/Browser-Session-Id (hodor's
  // embed middleware requires the former and strips the latter).
  if (isEmbedMode()) {
    return createEmbedHeaders()
  }
  const auth: RUserSession = getLocalAuth()
  return {
    'Content-Type': 'application/json',
    'Client-Type': 'web_app',
    Aom_uuid: auth.aom_uuid,
    Authorization: `Token ${auth.auth_token}`,
    'Browser-Session-Id': auth.browser_session_id,
    Region: auth.region,
  }
}

/**
 * The active region — from the sessionStorage embed session in embed mode,
 * otherwise from localStorage['auth']. Single home for the "region source
 * depends on mode" rule shared by resolveBaseUrl and the SSE stream.
 */
export const getActiveRegion = (): string | undefined =>
  isEmbedMode() ? getEmbedSession()?.region : getLocalAuth().region

/**
 * The acting ACTOR's aom uuid — same mode-dependent rule as getActiveRegion.
 *
 * `localStorage['auth']` is written by console login, the settings profile save
 * and billing, none of which run in the iframe, so `getLocalAuth().aom_uuid` is
 * `undefined` there and every ownership comparison against it silently fails.
 * The embed session carries the value hodor minted for the session's actor.
 */
export const getActiveAomUuid = (): string | undefined =>
  isEmbedMode() ? getEmbedSession()?.aom_uuid : getLocalAuth().aom_uuid

/**
 * Resolves the base URL with region replacement for direct fetch calls.
 * This is useful when making fetch requests that bypass axios interceptors.
 * @param regional - Whether to use regional API URL (default: true)
 * @returns The resolved base URL with region placeholder replaced
 */
export const resolveBaseUrl = (regional = true): string => {
  let baseURL = (regional ? REGIONAL_API_URL : GLOBAL_API_URL) || ''

  if (baseURL.includes('{{region}}')) {
    const region = getActiveRegion()
    if (!region) {
      throw new Error('Region is not set, cannot resolve base URL')
    }
    baseURL = baseURL.replace('{{region}}', region)
  }

  return baseURL
}

// Create a new service instance
/**
 * Creates a service instance with axios for making HTTP requests.
 * @param resource - The resource location for the service.
 * @param regional - A boolean indicating whether to use the regional API URL or the global API URL.
 * @returns The service instance with REST methods.
 * @example
 * const service = createService('users')
 * service.get<User[]>('list', { page: 1, limit: 10 })
 */
/**
 * Does this 401 mean "THIS resource refused you", rather than "your session is
 * gone"? Pure, and takes `embed` as an argument rather than reading the mode, so
 * both arms are testable without touching window.location (failure mode 40).
 *
 * The distinction is load-bearing twice over: in the console a false positive
 * logs the user out, and in embed it tears the iframe down — which the loader
 * answers by minting a NEW session, so a route that 401s on every boot walks the
 * account into hodor's per-account cap and locks it out.
 *
 * '/verify/' matches only the Verify product endpoints — the auth flows are
 * hyphenated (verify-otp, verify-user) and stay on the logout path.
 *
 * The two embed-only entries are gated rather than added unconditionally because
 * the console's logout behaviour is out of scope: exempting those routes there
 * may well be correct, but it changes when a console user gets logged out, which
 * is a separate decision from shipping embed.
 *   - external-apps-service: deploy skew. hodor's embed gateway maps upstream
 *     401→403 centrally, but an older gateway forwards plivocx's AOM 401
 *     verbatim, and that must never tear down the iframe.
 *   - rag-service: a refused knowledge-base read, and REACHED on a healthy boot —
 *     the regional session becomes readable a beat after exchange, so a KB call
 *     landing in that gap 401s transiently.
 *
 * KNOWN WEAKNESS, and the reason this is a list rather than a rule: enumerating
 * routes is guessing. It has grown twice, each time straight after an incident,
 * and every proxied route nobody thought of is a latent account lockout. The
 * durable shape is to stop guessing and ask the authority — on an embed 401,
 * re-probe v1/embed/session/me once: alive means the 401 was resource-scoped,
 * gone means the session really died. That deletes this list and cannot be wrong
 * about a route it never heard of. Deliberately not done here (it changes
 * teardown semantics mid-hotfix); tracked as the next embed ticket.
 */
const RESOURCE_SCOPED_401_PATHS = ['plivo-api', '/tdlc/', '/verify/']
const EMBED_RESOURCE_SCOPED_401_PATHS = ['external-apps-service', 'rag-service']

export const isResourceScoped401Url = (url: string | undefined, embed: boolean): boolean => {
  if (!url) {
    return false
  }
  const paths = embed
    ? [...RESOURCE_SCOPED_401_PATHS, ...EMBED_RESOURCE_SCOPED_401_PATHS]
    : RESOURCE_SCOPED_401_PATHS
  return paths.some((path) => url.includes(path))
}

export function createService(
  resource = '',
  regional = true,
  backendServiceName = BackendServiceName.default,
  version = '',
  domain = '',
): Service {
  const baseURL = `${
    regional ? REGIONAL_API_URL : GLOBAL_API_URL
  }${version ? `${version}/` : ''}${backendServiceName ? `${backendServiceName}` : ''}${resource}`
  const instance = axios.create({
    baseURL: domain || baseURL,
    timeout: 60000, // 60 seconds - prevents "timeout of 0ms exceeded" errors
  })

  // Interceptors
  // Request interceptor to append the global headers
  instance.interceptors.request.use(
    (config) => {
      // Embed iframe mode: rewrite onto /v1/embed, resolve {{region}} from
      // the embed session, and force the embed headers. No-op otherwise.
      if (isEmbedMode()) {
        return applyEmbedRequestConfig(config)
      }
      const auth = getLocalAuth()

      if (config.baseURL?.includes('{{region}}') && !auth.region) {
        throw new Error('Region is not set, cannot make request')
      }

      // Per-org opt-in: on /tdlc/ calls the flag is the sole authority over X-TDLC-Transport (set
      // when enrolled, strip any caller casing otherwise). Off (default) => absent => SDK routing.
      return {
        ...config,
        baseURL: config.baseURL?.replace('{{region}}', auth.region),
        headers: applyTdlcTransport(
          { ...createGlobalHeaders(), ...config.headers },
          config.url,
          isTdlcApiTransportEnabled(),
        ),
      }
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const isLogoutUrl = error.response?.config.url?.includes('logout')
      const isLoginUrl = error.response?.config.url?.includes('login')
      const isAuthFlowUrl =
        error.response?.config.url?.includes('phone-verification') ||
        error.response?.config.url?.includes('verify-otp') ||
        error.response?.config.url?.includes('activate-invite')
      // Resource-level 401s (Plivo proxy / 10DLC / Verify / external-apps) mean
      // the org/subaccount credential or AOM check failed for THIS resource, not
      // that the session expired. Let the mutation onError surface a toast
      // instead of logging out.
      // '/verify/' matches only the Verify product endpoints — the auth flows are
      // hyphenated (verify-otp, verify-user) and stay on the logout path above.
      // external-apps-service is listed for deploy skew: hodor's embed gateway
      // maps upstream 401→403 centrally, but an older gateway would forward
      // plivocx's AOM 401 verbatim, and that must never tear down the iframe.
      const isResourceScoped401 = isResourceScoped401Url(error.response?.config.url, isEmbedMode())

      // Embed iframe mode: a 401 means the embed session expired/revoked.
      // The overlay + parent postMessage handle it — NEVER window.cx.logout,
      // which would wipe a real console session in another tab. Resource-level
      // 401s (plivo-api / 10DLC) are exempted here too, exactly as for the
      // console logout path below: they mean THIS resource failed, not that the
      // session died, so they must not tear down the whole iframe.
      if (!isResourceScoped401 && handleEmbedUnauthorized(error.response?.status)) {
        return Promise.reject(new Error('Response Interceptor - 401 - embed session expired'))
      }

      if (
        error.response?.status === 401 &&
        !isLogoutUrl &&
        !isLoginUrl &&
        !isAuthFlowUrl &&
        !isResourceScoped401
      ) {
        window.cx.logout(true)
        return Promise.reject(new Error('Response Interceptor - 401 Unauthorized - Logging out'))
      }

      // Ignore common client-side errors that are part of normal flow
      const ignoredStatusCodes = [
        400, // Bad Request — server validation; UI surfaces via toast
        401, // Unauthorized
        403, // Forbidden
        404, // Not Found
        422, // Unprocessable Entity
        409, // Conflict
        429, // Too Many Requests
      ]

      if (!ignoredStatusCodes.includes(error.response?.status)) {
        // If the error is a 500 error, log it as a critical error
        const is500Error = error.response?.status >= 500
        logger[is500Error ? 'critical' : 'error'](error, {
          url: error.response?.config.url,
          status: error.response?.status,
          data: error.response?.data,
        })
      }

      return Promise.reject(error)
    },
  )

  // Default REST methods
  const methods: Omit<Service, 'instance' | 'stream'> = {
    get: (path, params, config = {}) =>
      instance.get(path, { params, ...config }).then((res) => res.data),
    post: (path, data, params, config = {}) =>
      instance.post(path, data, { params, ...config }).then((res) => res.data),
    put: (path, data) => instance.put(path, data).then((res) => res.data),
    patch: (path, data) => instance.patch(path, data).then((res) => res.data),
    delete: (path, data, config = {}) =>
      instance.delete(path, { data, ...config }).then((res) => res.data),
  }

  // Add SSE streaming method
  const stream: StreamFunction = (path, body, handlers) =>
    createSSEStream(instance.defaults.baseURL || '', path, body, handlers)

  return {
    instance,
    ...methods,
    stream,
  }
}

export const extractBlob = (response: unknown): Blob => {
  if (response instanceof Blob) return response
  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    response.data instanceof Blob
  ) {
    return response.data
  }
  throw new Error(`Expected Blob response, got ${typeof response}`)
}
